import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, ensureCsrfCookie, get, post, tenantStore, tokenStore } from "./api";
import { bus } from "./bus";
import { portalSessionHint } from "./portalAuth";
import { resolveTenantHeader } from "./tenantHeader";
import { clearStoredImpersonation } from "./impersonation";
import type { LoginResponse, MeResponse, User } from "./types";

interface AuthState {
  loading: boolean;
  user: User | null;
  roles: string[];
  permissions: string[];
  /** True after login when 2FA is required but not yet completed. */
  twoFactorRequired: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, code?: string) => Promise<{ twoFactorRequired: boolean; portal: boolean }>;
  challenge2fa: (code?: string, recoveryCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TWO_FACTOR_FLAG = "control.2fa_pending";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    loading: true,
    user: null,
    roles: [],
    permissions: [],
    twoFactorRequired: localStorage.getItem(TWO_FACTOR_FLAG) === "1",
  });

  // SSO callback. As of M7 the callback establishes a session *cookie*
  // server-side (like normal login) and no longer puts a bearer token in the
  // URL fragment — so there's nothing to stash. We only read the `two_factor`
  // hint to show the challenge immediately (refresh()/me reports it
  // authoritatively too). The `token` harvest is kept purely for backward
  // compatibility with a not-yet-deployed pre-M7 backend.
  useEffect(() => {
    if (!window.location.hash || window.location.hash.length < 2) return;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const isSsoReturn = params.get("sso") === "1" || params.has("token");
    if (!isSsoReturn) return;
    const legacyToken = params.get("token");
    if (legacyToken) tokenStore.set(legacyToken); // pre-M7 backend fallback only
    if (params.get("two_factor_required") === "1") {
      localStorage.setItem(TWO_FACTOR_FLAG, "1");
    }
    // Drop the hash so back/forward + refresh don't replay it.
    window.history.replaceState({}, "", window.location.pathname + window.location.search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async () => {
    // Cookie auth (audit C7): we no longer require a localStorage token
    // to attempt /me. The session cookie may already be set from a
    // previous tab; calling /me lets the server tell us if it's still
    // valid. A 401 → not signed in.
    try {
      let me = await get<MeResponse>("/v1/auth/me");
      // First-login auto-pick: a freshly-provisioned user has
      // current_tenant_id=null. If they belong to exactly one tenant,
      // switch into it transparently — without this, the UI shows
      // "No tenant" with no way for the user to escape it (every
      // tenant-scoped API call 409s before the user even sees the
      // dashboard). For multi-tenant users we leave them on the picker
      // so they choose intentionally.
      const tenants = me.user?.tenants ?? [];
      if (!me.user.current_tenant_id && tenants.length === 1) {
        try {
          await post("/v1/tenants/switch", { tenant_id: tenants[0].id });
          me = await get<MeResponse>("/v1/auth/me");
        } catch {
          // Best-effort. If the switch fails (membership got revoked
          // mid-flight, etc.) we let the picker handle it manually.
        }
      }
      // Keep this tab's tenant if the user still belongs to it; otherwise fall
      // back to the server's current tenant, then any membership. A stale id
      // left behind after removal made every call 403 while the UI showed
      // another tenant (audit FE-15).
      tenantStore.set(resolveTenantHeader(
        tenantStore.get(),
        (me.user.tenants ?? []).map((t) => t.id),
        me.user.current_tenant_id ?? null,
        !!me.user.is_super_admin,
      ));
      // The server tells us authoritatively whether a 2FA challenge is still
      // owed (covers cookie + SSO logins, which carry no body token) — audit M7.
      const pending = me.two_factor_pending ?? false;
      setState({
        loading: false,
        user: me.user,
        roles: me.roles,
        permissions: me.permissions,
        twoFactorRequired: pending,
      });
      if (pending) localStorage.setItem(TWO_FACTOR_FLAG, "1");
      else localStorage.removeItem(TWO_FACTOR_FLAG);
    } catch (err) {
      if (err instanceof ApiError && err.code === "two_factor_required") {
        setState((s) => ({ ...s, loading: false, twoFactorRequired: true }));
        localStorage.setItem(TWO_FACTOR_FLAG, "1");
        return;
      }
      // Session/token rejected — clear any leftover bearer token.
      tokenStore.set(null);
      tenantStore.set(null);
      localStorage.removeItem(TWO_FACTOR_FLAG);
      setState({ loading: false, user: null, roles: [], permissions: [], twoFactorRequired: false });
    }
  }, []);

  // Bootstrap: ensure CSRF cookie + try to identify the user from the
  // session before rendering any guarded UI.
  useEffect(() => {
    void (async () => {
      await ensureCsrfCookie();
      await refresh();
    })();
  }, [refresh]);

  // Session-expired fan-out from the api client. When any authed
  // request 401s mid-session, the api layer clears local tokens and
  // emits `auth:expired`; we drop the cached user so RequireAuth
  // bounces the next render to /auth/login. Without this the SPA
  // keeps showing the previous screen with empty data because no
  // single component knew the cookie had died.
  useEffect(() => {
    return bus.on("auth:expired", () => {
      setState({ loading: false, user: null, roles: [], permissions: [], twoFactorRequired: false });
    });
  }, []);

  const login = useCallback<AuthContextValue["login"]>(async (email, password, code) => {
    await ensureCsrfCookie({ force: true });
    const res = await post<LoginResponse>("/v1/auth/login", code ? { email, password, code } : { email, password }, { anon: true });
    if (res.portal) {
      // A client account: the server set the portal session cookie.
      if (!res.two_factor_required) portalSessionHint.set(true);
      return { twoFactorRequired: !!res.two_factor_required, portal: true };
    }
    if (res.user?.current_tenant_id) tenantStore.set(res.user.current_tenant_id);
    if (res.two_factor_required) {
      localStorage.setItem(TWO_FACTOR_FLAG, "1");
      setState((s) => ({ ...s, twoFactorRequired: true, loading: false }));
      return { twoFactorRequired: true, portal: false };
    }
    localStorage.removeItem(TWO_FACTOR_FLAG);
    await refresh();
    return { twoFactorRequired: false, portal: false };
  }, [refresh]);

  const challenge2fa = useCallback<AuthContextValue["challenge2fa"]>(async (code, recoveryCode) => {
    // Same as login — the response token is ignored; the session cookie
    // is bumped to 2fa-verified server-side.
    await post<{ token: string }>("/v1/auth/2fa/challenge",
      code ? { code } : { recovery_code: recoveryCode });
    localStorage.removeItem(TWO_FACTOR_FLAG);
    setState((s) => ({ ...s, twoFactorRequired: false }));
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await post("/v1/auth/logout"); } catch { /* ignore */ }
    tokenStore.set(null);
    tenantStore.set(null);
    clearStoredImpersonation();
    localStorage.removeItem(TWO_FACTOR_FLAG);
    setState({ loading: false, user: null, roles: [], permissions: [], twoFactorRequired: false });
  }, []);

  const value = useMemo<AuthContextValue>(() => ({ ...state, login, challenge2fa, logout, refresh }),
    [state, login, challenge2fa, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
